/* ls-stade-motion-experience — "CHOISIR SON EXPÉRIENCE" scene motion on the
 * native aiconeo section (p70). Runner + contract: assets/ls-stade-motion.js.
 *
 * Native structure it recognises (element TYPES and order, never text/ids):
 *   section content blocks, in order:
 *     intro block(s)  — anything (the title + text container today)
 *     LAST block      — ONE content-slider (classic layout, not rail) whose
 *                       slides are image-with-children frames, one per
 *                       experience (legacy form: a container whose only
 *                       children are those frames); each frame's content
 *                       layer holds exactly ONE container = the card (its
 *                       colours are the builder's own fields).
 *   Anything else → the native page is left untouched.
 *
 * While motion is on the frames are re-parented out of the slider into a
 * runtime stage (div.ls-exp__stage > div.ls-exp__track) and the native
 * `.aico-neo-section__outer` — which keeps the emptied slider shell — is
 * hidden, so the slider's arrows/dots/swipe/keys have nothing to act on and
 * its IntersectionObserver keeps any autoplay paused (the carousel scene's
 * approach). Without motion (reduced motion, no JS) the slider is the page.
 *
 * ≥750px and ≥2 frames — the original's pinned scrub (ls-stade-experience.js):
 *   the section pins below the header at screen height; the frames become a
 *   horizontal track that slides one viewport per experience; the intro stays
 *   put top-left; the cards are lifted out of their photos into one grid cell
 *   on the right and cross-fade (outgoing lifts out, incoming rises) at the
 *   mid-point between frames; decorative "01 / 02 / 03" pagers (from the frame
 *   COUNT) sit in each card and progress bars bottom-left follow the frame.
 * <750px — no pin (the original's static fallback): the frames are unrolled
 *   as stacked banners with their card, the intro is laid over the first
 *   photo and the cards get their pager.
 * 1 frame → no pin at any width (the phone layout still overlays the intro).
 * Reduced motion / no GSAP / unknown structure → nothing is applied.
 *
 * Everything added here is reverted by the gsap.matchMedia cleanup (nodes are
 * moved back to their recorded places, decorative nodes removed).
 */
(function () {
  'use strict';
  if (!window.LsStadeMotion) return;

  var SCROLL_PER_FRAME = 0.75; /* viewport heights of scroll per frame change */
  var FADE = 0.3;              /* cross-fade length, in frame units */

  function toArray(list) { return Array.prototype.slice.call(list || []); }
  function elementKids(node) {
    return toArray(node.children).filter(function (c) { return c.tagName !== 'STYLE' && c.tagName !== 'SCRIPT'; });
  }
  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function measureHeaderOffset() {
    var header = document.querySelector('.shopify-section-header, .section-header');
    if (!header) return 0;
    var height = header.getBoundingClientRect().height;
    var bar = header.querySelector('sticky-header, .header-wrapper, .header');
    if (bar) height = Math.max(height, bar.getBoundingClientRect().height);
    return Math.round(height);
  }

  function isFrame(el) {
    return !!el && el.classList.contains('content-type-neo-image--overlay');
  }

  /* A slide's image element: the slide's only element, possibly behind the
     renderer's anonymous `div[id]:not([class])` wrapper. */
  function slideFrame(slide) {
    var kids = elementKids(slide);
    if (kids.length !== 1) return null;
    var el = kids[0];
    if (!isFrame(el) && el.tagName === 'DIV' && !el.className) {
      kids = elementKids(el);
      if (kids.length !== 1) return null;
      el = kids[0];
    }
    return isFrame(el) ? el : null;
  }

  function recognise(section) {
    var outer = elementKids(section).filter(function (c) { return c.classList.contains('aico-neo-section__outer'); })[0];
    var content = outer && outer.querySelector('.aico-neo-section__content');
    if (!content) return null;
    var blocks = toArray(content.querySelectorAll('.content-row > .content-column > .content'))
      .filter(function (b) { return !b.parentElement.closest('.content-type-neo-container, .content-type-neo-image, .neo-slider'); });
    if (!blocks.length) return null;
    var block = blocks[blocks.length - 1];
    var shell = elementKids(block);
    if (shell.length !== 1) return null;
    shell = shell[0];

    var frames;
    if (shell.classList.contains('neo-slider')) {
      if (shell.getAttribute('data-neo-slider-layout') === 'rail') return null;
      var sliderTrack = shell.querySelector('.neo-slider__track');
      if (!sliderTrack) return null;
      var slides = elementKids(sliderTrack);
      if (!slides.length || slides.some(function (sl) { return !sl.classList.contains('neo-slider__slide'); })) return null;
      frames = slides.map(slideFrame);
    } else if (shell.classList.contains('content-type-neo-container')) {
      frames = elementKids(shell); /* legacy: a plain container of frames */
    } else {
      return null;
    }
    if (!frames.length || frames.some(function (f) { return !isFrame(f); })) return null;

    var cards = [];
    for (var i = 0; i < frames.length; i++) {
      var layer = frames[i].querySelector(':scope > .neo-image__overlay');
      if (!layer) return null;
      var kids = elementKids(layer);
      if (kids.length !== 1 || !kids[0].classList.contains('content-type-neo-container')) return null;
      cards.push(kids[0]);
    }
    return { intro: blocks.slice(0, -1), frames: frames, cards: cards };
  }

  /* Remember where a node lives so the cleanup can put it back exactly. */
  function place(node) { return { node: node, parent: node.parentNode, next: node.nextSibling }; }
  function restore(p) {
    if (!p.parent) return;
    p.parent.insertBefore(p.node, p.next && p.next.parentNode === p.parent ? p.next : null);
  }

  function decor(cls) {
    var el = document.createElement('div');
    el.className = cls;
    return el;
  }

  /* Decorative pager "01 / 02 / 03" with this card's number current, derived
     from the frame count; ink = the card title's own (builder) colour. */
  function addPagers(s, added) {
    var n = s.cards.length;
    if (n < 2) return;
    s.cards.forEach(function (card, i) {
      var p = document.createElement('p');
      p.className = 'ls-exp__pager';
      p.setAttribute('aria-hidden', 'true');
      for (var k = 0; k < n; k++) {
        if (k) {
          var sep = document.createElement('span');
          sep.className = 'ls-exp__pager-sep';
          sep.textContent = '/';
          p.appendChild(sep);
        }
        var num = document.createElement('span');
        num.className = 'ls-exp__pager-num' + (k === i ? ' is-current' : '');
        num.textContent = pad(k + 1);
        p.appendChild(num);
      }
      var ink = card.querySelector('h1, h2, h3, h4, h5, h6') || card.querySelector('.content-type-neo-text');
      if (ink) p.style.color = getComputedStyle(ink).color;
      card.insertBefore(p, card.firstChild);
      added.push(p);
    });
  }

  function introLayer(s, section, places, added) {
    if (!s.intro.length) return null;
    var layer = decor('ls-exp__intro');
    s.intro.forEach(function (b) { places.push(place(b)); layer.appendChild(b); });
    section.appendChild(layer);
    added.push(layer);
    return layer;
  }

  /* Frames out of the slider (or legacy container) into the runtime stage;
     the native outer (slider shell included) is hidden by the state class. */
  function stageLayer(s, section, places, added) {
    var stage = decor('ls-exp__stage');
    var track = decor('ls-exp__track');
    stage.appendChild(track);
    s.frames.forEach(function (f) { places.push(place(f)); track.appendChild(f); });
    section.appendChild(stage);
    added.push(stage);
    return track;
  }

  function undo(places, added) {
    for (var k = places.length - 1; k >= 0; k--) restore(places[k]);
    added.forEach(function (node) { if (node.parentNode) node.parentNode.removeChild(node); });
  }

  /* The inline props GSAP writes (incl. the translate/rotate/scale `none`
     stamps clearProps leaves behind): saved before, put back after, so a
     builder-set value (e.g. an opacity field) survives a revert. */
  var TWEENED = ['transform', 'opacity', 'visibility', 'translate', 'rotate', 'scale', 'will-change'];
  function saveInline(el) {
    var saved = {};
    TWEENED.forEach(function (k) { saved[k] = el.style.getPropertyValue(k); });
    return function () {
      TWEENED.forEach(function (k) {
        if (saved[k]) el.style.setProperty(k, saved[k]);
        else el.style.removeProperty(k);
      });
    };
  }

  /* ---- ≥750px: pinned horizontal scrub ---- */
  function pinned(section, s) {
    var places = [];
    var added = [];
    var n = s.frames.length;
    var last = n - 1;
    var active = -1;

    var restoreInline = s.cards.map(saveInline);
    section.classList.add('ls-exp--pin');
    s.frames.forEach(function (f) {
      var img = f.querySelector(':scope > img');
      if (img) img.loading = 'eager'; /* clipped off to the right: lazy would wait for the slide */
    });

    introLayer(s, section, places, added);
    var track = stageLayer(s, section, places, added);

    var cardLayer = decor('ls-exp__cards');
    s.cards.forEach(function (c) { places.push(place(c)); cardLayer.appendChild(c); });
    section.appendChild(cardLayer);
    added.push(cardLayer);
    addPagers(s, added);

    var bars = decor('ls-exp__bars');
    bars.setAttribute('aria-hidden', 'true');
    for (var b = 0; b < n; b++) bars.appendChild(decor('ls-exp__bar'));
    section.appendChild(bars);
    added.push(bars);
    var barEls = toArray(bars.children);

    function setActive(index) {
      if (index === active) return;
      active = index;
      s.cards.forEach(function (c, i) {
        if (i === index) c.removeAttribute('aria-hidden');
        else c.setAttribute('aria-hidden', 'true');
      });
      barEls.forEach(function (bar, i) { bar.classList.toggle('is-active', i === index); });
    }

    /* The pinned box = the screen below the fixed header. */
    function setOffset() {
      section.style.setProperty('--ls-exp-offset', measureHeaderOffset() + 'px');
    }
    setOffset();

    gsap.set(s.cards, { autoAlpha: 0, y: 0 });
    gsap.set(s.cards[0], { autoAlpha: 1 });
    setActive(0);

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: function () { return 'top ' + measureHeaderOffset() + 'px'; },
        end: function () { return '+=' + Math.round(window.innerHeight * SCROLL_PER_FRAME * last); },
        scrub: true,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onRefreshInit: setOffset,
        onUpdate: function (self) { setActive(Math.round(self.progress * last)); }
      }
    });
    /* The slide: one linear sweep; frame i is settled at time i. */
    tl.to(track, { xPercent: -100 * last, duration: last, ease: 'none' }, 0);
    /* Cards cross-fade centred on each boundary (i + 0.5). */
    for (var i = 0; i < last; i++) {
      var at = i + 0.5 - FADE / 2;
      tl.to(s.cards[i], { autoAlpha: 0, y: -16, duration: FADE, ease: 'none' }, at);
      tl.fromTo(s.cards[i + 1], { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: FADE, ease: 'none' }, at);
    }

    return function cleanup() {
      /* gsap.matchMedia reverts the timeline, its pin and the inline props. */
      section.classList.remove('ls-exp--pin');
      section.style.removeProperty('--ls-exp-offset');
      s.cards.forEach(function (c) { c.removeAttribute('aria-hidden'); });
      restoreInline.forEach(function (fn) { fn(); });
      undo(places, added);
    };
  }

  /* ---- <750px: static banners, intro over the first photo ---- */
  function banners(section, s) {
    var places = [];
    var added = [];
    var nativeH = s.frames[0].getBoundingClientRect().height;
    section.classList.add('ls-exp--banners');
    var layer = introLayer(s, section, places, added);
    stageLayer(s, section, places, added);
    addPagers(s, added);
    var ro = null;
    function sync() {
      section.style.setProperty('--ls-exp-intro-h', Math.ceil(layer.getBoundingClientRect().height) + 'px');
    }
    var lastH = -1;
    if (layer) {
      s.frames[0].classList.add('ls-exp__first');
      section.style.setProperty('--ls-exp-f1-h', Math.round(nativeH) + 'px');
      sync();
      if (window.ResizeObserver) {
        ro = new ResizeObserver(function () {
          var h = Math.ceil(layer.getBoundingClientRect().height);
          if (h === lastH) return;
          var first = lastH < 0;
          lastH = h;
          sync();
          /* Later pins below moved: remeasure (the runner refreshes after init). */
          if (!first && window.ScrollTrigger) ScrollTrigger.refresh();
        });
        ro.observe(layer);
      }
    }
    return function cleanup() {
      if (ro) ro.disconnect();
      s.frames[0].classList.remove('ls-exp__first');
      section.classList.remove('ls-exp--banners');
      section.style.removeProperty('--ls-exp-intro-h');
      section.style.removeProperty('--ls-exp-f1-h');
      undo(places, added);
    };
  }

  LsStadeMotion.scene('experience', function (section, ctx) {
    if (ctx.reduced || !ctx.mm) return null;
    var s = recognise(section);
    if (!s) return null;
    ctx.mm.add({ desk: '(min-width: 750px)', phone: '(max-width: 749px)' }, function (c) {
      if (c.conditions.desk) return s.frames.length > 1 ? pinned(section, s) : undefined;
      return banners(section, s);
    });
    return null;
  });
})();
